import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { firebaseAuth } from "../lib/firebase";
import { usePaymentPolling } from "../hooks/usePaymentPolling";
import { PAYMENT_TYPE, SUBSCRIPTION_DISPLAY_PRICE } from "../lib/constants";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { toast } from "sonner";
import {
  initiatePayment,
  verifyPayment,
  loadRazorpayScript,
  openRazorpayCheckout,
} from "../services/paymentService";
import {
  CreditCard,
  CheckCircle2,
  Loader2,
  ArrowLeft,
  Clock,
  Shield,
  Sparkles,
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SubscribePage = () => {
  const navigate = useNavigate();
  const { user, updateUser } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [paymentPending, setPaymentPending] = useState(false);

  // ── Polling: watches for webhook-driven subscription activation ─────────
  usePaymentPolling({
    paymentPending,
    setPaymentPending,
    validStatuses: ["active"],
    onSuccess: async () => {
      // Refresh user and go to dashboard
      const freshToken = await firebaseAuth.currentUser?.getIdToken(true);
      const res = await fetch(`${API_URL}/api/me`, {
        headers: { Authorization: `Bearer ${freshToken}` },
      });
      if (res.ok) {
        const userData = await res.json();
        updateUser(userData);
      }
      toast.success("Subscription activated! Welcome back.");
      navigate("/dashboard");
    },
  });

  const handleSubscribe = async () => {
    setIsLoading(true);
    try {
      const token = await firebaseAuth.currentUser?.getIdToken();

      const paymentData = await initiatePayment(token, PAYMENT_TYPE, {
        receipt: `subscribe_${user?.uid || "unknown"}_${Date.now()}`,
      });

      const scriptLoaded = await loadRazorpayScript();
      if (!scriptLoaded) {
        toast.error("Failed to load payment SDK. Please check your internet connection.");
        setIsLoading(false);
        return;
      }

      setIsLoading(false);

      openRazorpayCheckout(
        PAYMENT_TYPE,
        paymentData,
        {
          name: user?.name || "",
          email: user?.email || "",
          contact: user?.phone || "",
        },
        async (razorpayResponse) => {
          setIsLoading(true);
          try {
            const freshToken = await firebaseAuth.currentUser?.getIdToken(true);
            const result = await verifyPayment(freshToken, PAYMENT_TYPE, razorpayResponse);

            if (
              result.payment_status === "captured" ||
              result.payment_status === "active"
            ) {
              toast.success("Subscription activated! Welcome back.");
              // Refresh user data
              const meRes = await fetch(`${API_URL}/api/me`, {
                headers: { Authorization: `Bearer ${freshToken}` },
              });
              if (meRes.ok) {
                const userData = await meRes.json();
                updateUser(userData);
              }
              navigate("/dashboard");
            } else {
              setIsLoading(false);
              setPaymentPending(true);
            }
          } catch (verifyErr) {
            toast.error(verifyErr.message || "Payment verification failed. Please contact support.");
            setIsLoading(false);
          }
        },
        (err) => {
          toast.error(err.message || "Payment failed. Please try again.");
        }
      );
    } catch (err) {
      toast.error(err.message || "Could not initiate payment. Please try again.");
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <header className="p-4 border-b border-border/50">
        <div className="max-w-2xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 flex items-center justify-center">
              <img src="/logo.svg" alt="Nutri Saathi Logo" className="w-10 h-10 object-contain" />
            </div>
            <span className="font-bold text-lg text-foreground">Nutri Saathi</span>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate("/dashboard")}
            className="text-muted-foreground"
          >
            <ArrowLeft className="w-4 h-4 mr-1" />
            Dashboard
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center px-4 py-8">
        <div className="w-full max-w-lg">
          <Card className="border-border/50 shadow-lg">
            {paymentPending ? (
              /* ── Pending: waiting for webhook ── */
              <>
                <CardHeader className="text-center">
                  <div className="w-14 h-14 mx-auto rounded-2xl bg-primary/10 flex items-center justify-center mb-4">
                    <Loader2 className="w-7 h-7 text-primary animate-spin" />
                  </div>
                  <CardTitle className="text-2xl">Confirming Your Payment</CardTitle>
                  <CardDescription>
                    Your payment was received. We're waiting for the bank to confirm — this usually takes a few seconds.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-5">
                  <div className="p-5 rounded-2xl bg-gradient-to-br from-primary/10 to-primary/5 border border-primary/20 text-center space-y-2">
                    <p className="text-sm font-medium text-foreground">Please keep this page open</p>
                    <p className="text-xs text-muted-foreground">
                      We'll automatically take you to your dashboard as soon as confirmation arrives.
                    </p>
                  </div>
                </CardContent>
              </>
            ) : (
              /* ── Subscribe card ── */
              <>
                <CardHeader className="text-center">
                  <div className="w-14 h-14 mx-auto rounded-2xl bg-primary/10 flex items-center justify-center mb-4">
                    <CreditCard className="w-7 h-7 text-primary" />
                  </div>
                  <CardTitle className="text-2xl">Continue with Nutri Saathi</CardTitle>
                  <CardDescription>
                    Your free trial has ended. Subscribe to keep your AI coaching, diet plans, and accountability calls active.
                  </CardDescription>
                </CardHeader>
                <CardContent className="space-y-6">
                  {/* Price card */}
                  <div className="p-6 rounded-2xl bg-gradient-to-br from-[#D48C70]/10 to-[#c67d61]/5 border-2 border-[#D48C70]/30 text-center space-y-3">
                    <div className="inline-flex items-center gap-1.5 bg-[#D48C70]/10 text-[#D48C70] font-semibold px-3 py-1 rounded-full text-[10px] uppercase tracking-wider">
                      <Sparkles className="w-3.5 h-3.5" />
                      Full Access
                    </div>
                    <p className="text-4xl font-extrabold text-foreground tracking-tight">
                      {SUBSCRIPTION_DISPLAY_PRICE}<span className="text-lg font-normal text-muted-foreground"> / month</span>
                    </p>
                  </div>

                  {/* Features */}
                  <ul className="space-y-3 text-sm text-foreground">
                    {[
                      "Personalised AI diet plan",
                      "Daily accountability calls",
                      "Real-time nutrition tracking",
                      "Cancel anytime — no lock-in",
                    ].map((feature) => (
                      <li key={feature} className="flex items-center gap-3 bg-secondary/20 p-2.5 rounded-lg border border-border/50">
                        <div className="bg-[#D48C70]/10 p-1 rounded-full text-[#D48C70]">
                          <CheckCircle2 className="w-4 h-4" />
                        </div>
                        <span className="font-medium">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  {/* Trust badges */}
                  <div className="flex items-center justify-center gap-4 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1"><Shield className="w-3.5 h-3.5" /> Secured by Razorpay</span>
                    <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> Cancel anytime</span>
                  </div>

                  {/* Subscribe button */}
                  <Button
                    onClick={handleSubscribe}
                    className="w-full bg-[#D48C70] hover:bg-[#c67d61] text-white rounded-xl h-12 text-base btn-primary"
                    disabled={isLoading}
                    data-testid="subscribe-pay-btn"
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Preparing payment...
                      </>
                    ) : (
                      <>
                        <CreditCard className="w-4 h-4 mr-2" />
                        Subscribe Now
                      </>
                    )}
                  </Button>
                </CardContent>
              </>
            )}
          </Card>
        </div>
      </main>
    </div>
  );
};

export default SubscribePage;
